import axios from "axios";
import { getApiBaseUrl } from "./getApiBaseUrl";

const BASE_URL = getApiBaseUrl();
const API_URL = `${BASE_URL}/`;

// Initialize Axios instance for auth operations
export const authApiService = axios.create({
  baseURL: API_URL,
  headers: {
    "Content-Type": "application/json",
  },
  // withCredentials: true, // Important for cookies
});

// Request Interceptor for Authorization
authApiService.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('accessToken');
    
    if (token && !config.skipAuth) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    return config;
  },
  (error) => Promise.reject(error)
);

// Response Interceptor for handling common errors
authApiService.interceptors.response.use(
  (response) => response,
  (error) => {
    console.error('API Error:', error);
    
    if (error.response?.status === 401) {
      // Attempt to refresh token or redirect to login
      if (window.location.pathname !== '/login') {
        // Clear credentials on auth error
        localStorage.removeItem('accessToken');
        localStorage.removeItem('user');
        window.location.href = "/login";
      }
    }
    
    return Promise.reject(error);
  }
);

// Authentication service functions
export const authService = {
  // Login function
  async login(email, password) {
    try {
      const response = await authApiService.post('/auth/login', {
        emailid: email,
        userpassword: password
      });
  
      // FIXED: Adjusted to match the actual API response structure
      if (response.data && response.data.statusCode === 200) {

        const { accessToken, emailid, role, userid, profile, isAdmin } = response.data.body.data;
        const fullName = (profile?.name || "").trim();

        localStorage.setItem('accessToken', accessToken);
        localStorage.setItem('user', JSON.stringify({
          email: emailid,
          role: role,
          userid: userid,
          name: fullName,
          isAdmin: !!isAdmin,
        }));
        localStorage.setItem('profile', JSON.stringify(profile));
        if (fullName) {
          localStorage.setItem('username', fullName);
        }
        return { 
          success: true, 
          data: {
            accessToken,
            email: emailid,
            role: role,
            userid: userid,
            name: fullName,
            isAdmin: !!isAdmin,
            profile,
          }
        };
      }
  
      return {
        success: false,
        message: response.data?.body?.message || 'Login failed'
      };
    } catch (error) {
      if (error.response) {
        return {
          success: false,
          message: error.response.data?.message || `Server error: ${error.response.status}`,
          status: error.response.status
        };
      } else if (error.request) {
        return {
          success: false,
          message: 'No response from server. Check if server is running.'
        };
      } else {
        return {
          success: false,
          message: `Request error: ${error.message}`
        };
      }
    }
  },

  // Signup function
  async signup(name, email, password) {
    try {
      const response = await authApiService.post('/auth/signup', {
        name,
        email,
        password
      });
      

      if (response.data && response.data.success) {
        if (response.data.data && response.data.data.accessToken) {
          localStorage.setItem('accessToken', response.data.data.accessToken);
          localStorage.setItem('user', JSON.stringify({
            email: response.data.data.email,
            name: response.data.data.name
          }));
          
          return { success: true, data: response.data.data };
        }
        return { success: false, message: 'Invalid response format' };
      } else {
        return { success: false, message: response.data?.message || 'Signup failed' };
      }
    } catch (error) {
      if (error.response) {
        return { 
          success: false, 
          message: error.response.data?.message || `Server error: ${error.response.status}`,
          status: error.response.status
        };
      } else if (error.request) {
        return { success: false, message: 'No response from server' };
      } else {
        return { success: false, message: `Request error: ${error.message}` };
      }
    }
  },
  
  // Logout function
  async logout() {
    try {
      const response = await authApiService.post('/auth/logout');
      console.log("response logout",response)
      // Always clear local storage regardless of response
      localStorage.removeItem('accessToken');
      localStorage.removeItem('user');
      
      return { success: true };
    } catch (error) {
      // Still clear local storage on error
      localStorage.removeItem('accessToken');
      localStorage.removeItem('user');
      return { success: false, message: 'Error during logout' };
    }
  },
  
  // Refresh token function
  async refreshToken() {
    try {
      const response = await authApiService.post('/auth/refresh-token', null, {
        skipAuth: true // Skip adding the current token
      });
      
      if (response.data.success && response.data.data && response.data.data.accessToken) {
        localStorage.setItem('accessToken', response.data.data.accessToken);
        return { success: true, accessToken: response.data.data.accessToken };
      }
      
      return { success: false };
    } catch (error) {
      console.error('Token refresh error:', error);
      return { success: false };
    }
  },
  
  // Get current user function
  getCurrentUser() {
    const userStr = localStorage.getItem('user');
    if (!userStr) return null;
    
    try {
      return JSON.parse(userStr);
    } catch (error) {
      console.error('Error parsing user from localStorage', error);
      return null;
    }
  },
  
  // Check if user is authenticated
  isAuthenticated() {
    return !!localStorage.getItem('accessToken');
  }
};
